import React, { useEffect, useState } from 'react';
import './VirtualMallPurchase.scss';
import { virtualMallPointPurchaseListApi, virtualMallShopPurchaseListApi } from './VirtualMallApi/api.js';
import { imgBaseurl } from '../../baseUrl/baseurl.ts';
import { X, ExternalLink, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'react-toastify';

export default function VirtualMallPointPurchaseHistory() {
    const [subTab, setSubTab] = useState('points'); // 'points' or 'products'
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    const [pointSummary, setPointSummary] = useState({ total_amount: 0, total_orders: 0 });
    const [pointHistory, setPointHistory] = useState([]);
    
    const [productSummary, setProductSummary] = useState({ total_amount: 0, total_orders: 0 });
    const [productHistory, setProductHistory] = useState([]);

    const [showReceiptModal, setShowReceiptModal] = useState(false);
    const [selectedReceipt, setSelectedReceipt] = useState(null);
    const [failedImages, setFailedImages] = useState({});

    const getPointHistory = async () => {
        try {
            const referal_id = localStorage.getItem("virtual_referal_id");
            const data = {
                referal_id: referal_id,
                page: 1,
                limit: 100
            };
            const response = await virtualMallPointPurchaseListApi(data);
            if (response.status === true) {
                if (!response.data || response.data.length === 0) {
                    setPointHistory([]);
                } else {
                    setPointHistory(response.data);
                    setPointSummary({
                        total_amount: response.data.reduce((sum, item) => sum + Number(item.amount), 0),
                        total_orders: response.data.length
                    });
                }
            } else {
                toast.error(response.message || "Failed to fetch point purchase history");
            }
        } catch (error) {
            console.error("Error fetching point purchase list:", error);
        }
    };

    const getProductHistory = async () => {
        try {
            const referal_id = localStorage.getItem("virtual_referal_id");
            const data = {
                referal_id: referal_id
            };
            const response = await virtualMallShopPurchaseListApi(data);
            if (response.status === true) {
                if (!response.data || response.data.length === 0) {
                    setProductHistory([]);
                } else {
                    setProductHistory(response.data);
                    setProductSummary({
                        total_amount: response.total_amount || response.data.reduce((sum, item) => sum + Number(item.amount || item.vpp_total_points || 0), 0),
                        total_orders: response.total_orders || response.data.length
                    });
                }
            } else {
                toast.error(response.message || "Failed to fetch product purchase history");
            }
        } catch (error) {
            console.error("Error fetching product purchase list:", error);
        }
    };

    const handleViewReceipt = (receiptUrl, transactionId) => {
        if (!receiptUrl) return;
        
        let fullUrl = receiptUrl;
        if (!receiptUrl.startsWith('http://') && !receiptUrl.startsWith('https://')) {
            const cleanPath = receiptUrl.replace(/^\/?/, '');
            fullUrl = `${imgBaseurl}/${cleanPath}`;
        }
        const encodedUrl = encodeURI(fullUrl);
        console.log(encodedUrl, 'view_receipt');

        setSelectedReceipt({ url: encodedUrl, transactionId });
        setShowReceiptModal(true);
    };

    const closeReceiptModal = () => {
        setShowReceiptModal(false);
        setSelectedReceipt(null);
    };

    useEffect(() => {
        getPointHistory();
        getProductHistory();
    }, []);

    const activeHistory = subTab === 'points' ? pointHistory : productHistory;
    const activeSummary = subTab === 'points' ? pointSummary : productSummary;

    // Pagination computations
    const totalPages = Math.ceil(activeHistory.length / itemsPerPage);
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = activeHistory.slice(indexOfFirstItem, indexOfLastItem);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    return (
        <div className="dashboard-section">
            <h2>Virtual Mall Purchase</h2>

            {/* Sub-tabs toggles */}
            <div className="sub-tabs-container" style={{ display: 'flex', gap: '12px', marginBottom: '24px', borderBottom: '1px solid #e2e8f0', paddingBottom: '16px' }}>
                <button 
                    className={`sub-tab-btn ${subTab === 'points' ? 'active' : ''}`}
                    onClick={() => { setSubTab('points'); setCurrentPage(1); }}
                    style={{
                        background: subTab === 'points' ? '#667eea' : 'transparent',
                        color: subTab === 'points' ? '#fff' : '#64748b',
                        border: '1px solid',
                        borderColor: subTab === 'points' ? '#667eea' : '#e2e8f0',
                        padding: '10px 20px',
                        borderRadius: '8px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        boxShadow: subTab === 'points' ? '0 4px 6px rgba(102, 126, 234, 0.25)' : 'none'
                    }}
                >
                    🪙 Point Purchases
                </button>
                <button 
                    className={`sub-tab-btn ${subTab === 'products' ? 'active' : ''}`}
                    onClick={() => { setSubTab('products'); setCurrentPage(1); }}
                    style={{
                        background: subTab === 'products' ? '#667eea' : 'transparent',
                        color: subTab === 'products' ? '#fff' : '#64748b',
                        border: '1px solid',
                        borderColor: subTab === 'products' ? '#667eea' : '#e2e8f0',
                        padding: '10px 20px',
                        borderRadius: '8px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        boxShadow: subTab === 'products' ? '0 4px 6px rgba(102, 126, 234, 0.25)' : 'none'
                    }}
                >
                    🛍️ Product Purchases
                </button>
            </div>

            <div className="table-section">
                <div className="table-header">
                    <h3>{subTab === 'points' ? 'Your Point Purchases' : 'Your Product Orders'}</h3>
                    <div className="purchase-summary">
                        <div className="summary-item">
                            <span className="summary-label">Total Orders:</span>
                            <span className="summary-value">{activeSummary.total_orders}</span>
                        </div>
                        <div className="summary-item">
                            <span className="summary-label">Total Spent:</span>
                            <span className="summary-value">₹{Number(activeSummary.total_amount).toFixed(2)}</span>
                        </div>
                    </div>
                </div>
                
                <div className="table-responsive">
                    {subTab === 'points' ? (
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th>S.No</th>
                                    <th>Points</th>
                                    <th>Amount</th>
                                    <th>Transaction ID</th>
                                    <th>Status</th>
                                    <th>Cashback</th>
                                    <th>Date</th>
                                </tr>
                            </thead>
                            <tbody>
                                {currentItems.length > 0 ? (
                                    currentItems.map((order, index) => (
                                        <tr key={order.id || index}>
                                            <td>{indexOfFirstItem + index + 1}</td>
                                            <td>{order.point} pts</td>
                                            <td>₹{Number(order.amount).toFixed(2)}</td>
                                            <td>{order.transaction_id}</td>
                                            <td>
                                                <span className={`status-badge ${(order.purchase_status || 'pending').toLowerCase()}`}>
                                                    {order.purchase_status || 'Pending'}
                                                </span>
                                            </td>
                                            <td>
                                                <span style={{ color: '#166534', fontWeight: 'bold' }}>
                                                    {order.cashback_amount !== null && order.cashback_amount !== undefined ? `₹${Number(order.cashback_amount).toFixed(2)}` : '—'}
                                                </span>
                                            </td>
                                            <td>{new Date(order.created_at).toLocaleDateString()}</td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="7" style={{ textAlign: 'center', padding: '20px' }}>No point purchases found</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    ) : (
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th>S.No</th>
                                    <th>Image</th>
                                    <th>Product</th>
                                    <th>Date</th>
                                    <th>Amount</th>
                                    <th>Transaction ID</th>
                                    <th>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {currentItems.length > 0 ? (
                                    currentItems.map((order, index) => {
                                        const imagePath = order.vpp_product_image || order.vmproduct_image;
                                        const itemId = order.vpp_id || order.purchase_id || index;
                                        const productName = order.vpp_product_name || order.vmproduct_name || `Product ${order.vpp_product_id || order.product_id}`;
                                        const amount = order.vpp_total_points !== undefined ? order.vpp_total_points : (order.amount || 0);
                                        const transactionId = order.vpp_purchase_code || order.transaction_id || '-';
                                        const status = order.vpp_status || order.purchase_status || order.payment_status || 'Pending';

                                        return (
                                            <tr key={itemId}>
                                                <td>{indexOfFirstItem + index + 1}</td>
                                                <td>
                                                    {imagePath && !failedImages[itemId] ? (
                                                        <img 
                                                            src={`${imgBaseurl}/${imagePath.replace(/^\/?/, '')}`} 
                                                            alt={productName} 
                                                            style={{ width: '40px', height: '40px', borderRadius: '6px', objectFit: 'cover' }}
                                                            onError={() => setFailedImages(prev => ({ ...prev, [itemId]: true }))}
                                                        />
                                                    ) : (
                                                        <div style={{ width: '40px', height: '40px', borderRadius: '6px', backgroundColor: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px' }}>
                                                            📦
                                                        </div>
                                                    )}
                                                </td>
                                                <td>{productName}</td>
                                                <td>{order.created_at ? new Date(order.created_at).toLocaleDateString() : '-'}</td>
                                                <td>₹{Number(amount).toFixed(2)}</td>
                                                <td>{transactionId}</td>
                                                <td>
                                                    <span className={`status-badge ${status.toLowerCase()}`}>
                                                        {status}
                                                    </span>
                                                </td>
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan="7" style={{ textAlign: 'center', padding: '20px' }}>No product orders found</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    )}
                </div>

                {/* Pagination Controls */}
                {totalPages >= 1 && (
                    <div className="pagination-wrapper">
                        <div className="pagination-info">
                            Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, activeHistory.length)} of {activeHistory.length} entries
                        </div>
                        <div className="pagination-controls">
                            <button
                                className="page-btn"
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                                style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                            >
                                <ChevronLeft size={16} />
                            </button>

                            {[...Array(totalPages)].map((_, i) => {
                                if (totalPages > 7) {
                                    if (i + 1 === 1 || i + 1 === totalPages || (i + 1 >= currentPage - 2 && i + 1 <= currentPage + 2)) {
                                        return (
                                            <button
                                                key={i}
                                                className={`page-btn ${currentPage === i + 1 ? 'active' : ''}`}
                                                onClick={() => handlePageChange(i + 1)}
                                            >
                                                {i + 1}
                                            </button>
                                        );
                                    } else if (i + 1 === currentPage - 3 || i + 1 === currentPage + 3) {
                                        return <span key={i} className="pagination-ellipsis">...</span>;
                                    }
                                    return null;
                                }
                                return (
                                    <button
                                        key={i}
                                        className={`page-btn ${currentPage === i + 1 ? 'active' : ''}`}
                                        onClick={() => handlePageChange(i + 1)}
                                    >
                                        {i + 1}
                                    </button>
                                );
                            })}

                            <button
                                className="page-btn"
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === totalPages}
                                style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                            >
                                <ChevronRight size={16} />
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Receipt Modal */}
            {showReceiptModal && selectedReceipt && (
                <div className="modal-overlay" onClick={closeReceiptModal}>
                    <div className="modal-content receipt-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Payment Receipt</h2>
                            <button className="modal-close" onClick={closeReceiptModal}>
                                <X size={24} />
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="receipt-info-header">
                                <div className="receipt-transaction">
                                    <span className="label">Transaction ID:</span>
                                    <span className="value">{selectedReceipt.transactionId}</span>
                                </div>
                                <a
                                    href={selectedReceipt.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="web-view-link"
                                >
                                    <ExternalLink size={14} /> Full View
                                </a>
                            </div>
                        </div>
                        <div className="receipt-image-container">
                            <img src={selectedReceipt.url} alt="Payment Receipt" />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
